import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import styled, { css, ThemeContext } from 'styled-components';
import { hexToRGBA, noop } from '@lux/helpers';
import MaterialTabs from '@material-ui/core/Tabs';
import { Text, theme } from '@dls/web';

import slugify from '../../helpers/slugify';

import ChevronLeftIcon from '../../assets/svgs/chevron-left.svg';
import ChevronRightIcon from '../../assets/svgs/chevron-right.svg';

const StyledChevronRightIcon = styled(ChevronRightIcon)`
  path {
    fill: ${p => p.theme.colours.grey_400};
  }
`;
const StyledChevronLeftIcon = styled(ChevronLeftIcon)`
  path {
    fill: ${p => p.theme.colours.grey_400};
  }
`;

const StyledTabTitle = styled(Text)`
  ${p =>
    p.activeTab &&
    css`
      color: ${p => theme.main.colours.secondary.d1};
    `};
`;

const TabItem = styled.div`
  text-align: center;
  padding: ${p => p.theme.spacing(1)} ${p => p.theme.spacing(2)};
  color: ${p => p.theme.colours.tab_text_pri};

  cursor: pointer;
  transition: color 250ms ease-in-out, border-bottom-color 250ms ease-in-out;

  ${p => p.theme.media.md`
    display: inline-flex;
    align-items: center;
    padding: ${p.theme.spacing(1)} ${p.theme.spacing(5)};
  `};

  &:last-of-type {
    margin-right: 0;
  }

  ${p =>
    !p.activeTab &&
    css`
      opacity: 0.4;
    `};

  ${p =>
    p.disabled &&
    css`
      opacity: 0.4;
      pointer-events: none;
    `};

  ${p =>
    p.activeTab &&
    css`
      color: ${p => p.theme.colours.tab_text_pri_active};
    `};
`;

const StyledIcon = styled.div`
  ${p => p.theme.media.md`
    display: inline-block;
    margin-right: ${p.theme.spacing(1)};
  `};
`;

const StyledScrollButton = styled.div`
  width: ${p => p.theme.spacing(5)};
  cursor: pointer;
  display: ${p => (p.visible ? 'flex' : 'none')};
  align-items: center;
  justify-content: ${p =>
    p.direction === 'right' ? 'flex-end' : 'flex-start'};
  position: absolute;
  top: 0;
  bottom: 0;
  right: ${p => (p.direction === 'right' ? 0 : 'unset')};
  left: ${p => (p.direction === 'left' ? 0 : 'unset')};
  z-index: 999;
  margin-bottom: 2px;
  background-image: ${p => {
    const bgColor = p.theme.colours.tab_background_color || '#FFFFFF';
    const scrollIndicatorGradientColorStart = rgbaObjToCSSStr(
      hexToRGBA(bgColor),
      0.2
    );
    const scrollIndicatorGradientColorEnd = rgbaObjToCSSStr(
      hexToRGBA(bgColor),
      1
    );

    return p.direction === 'right'
      ? `linear-gradient(90deg, ${scrollIndicatorGradientColorStart} 0%, ${scrollIndicatorGradientColorEnd} 50%)`
      : `linear-gradient(270deg, ${scrollIndicatorGradientColorStart} 0%, ${scrollIndicatorGradientColorEnd} 50%)`;
  }};
`;

const rgbaObjToCSSStr = ({ r, g, b }, a) => {
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const CustomScrollButton = props => {
  const IconMap = {
    right: StyledChevronRightIcon,
    left: StyledChevronLeftIcon
  };
  const Icon = IconMap[props.direction];

  return (
    <StyledScrollButton
      visible={props.visible}
      direction={props.direction}
      onClick={props.onClick}
    >
      <Icon width={16} height={16} />
    </StyledScrollButton>
  );
};

const Tabs = props => {
  const { items, variant, scrollButtons, selectedTab, onTabChange } = props;

  const [activeTab, setActiveTab] = useState(selectedTab);
  const theme = useContext(ThemeContext);

  // Select the first tab as the default selected tab
  const defaultSelection = items[0].value;

  useEffect(() => {
    // Set a default selection if no selected tab is provided
    selectedTab ? setActiveTab(selectedTab) : setActiveTab(defaultSelection);
  }, [defaultSelection, selectedTab]);

  const handleTabChange = item => {
    if (!item.disabled) {
      setActiveTab(item.value);
      onTabChange(item);
    }
  };

  const activeIndex = items.map(i => i.value).indexOf(activeTab);

  return (
    <MaterialTabs
      style={{
        minHeight: 'unset',
        borderBottom: `1px solid ${theme.colours.tab_bottom_separator}`,
        position: 'relative'
      }}
      TabIndicatorProps={{
        style: { background: theme.colours.tab_text_pri_active }
      }}
      value={activeIndex > 0 ? activeIndex : 0}
      variant={variant}
      scrollButtons={scrollButtons}
      ScrollButtonComponent={CustomScrollButton}
    >
      {items.map(item => (
        <TabItem
          key={item.value}
          activeTab={item.value === activeTab}
          disabled={item.disabled}
          onClick={() => handleTabChange(item)}
          data-testid={`tab-${slugify(item.text)}`}
        >
          {item.icon && <StyledIcon>{item.icon}</StyledIcon>}

          <StyledTabTitle type="boldBody" activeTab={item.value === activeTab}>
            {item.text}
          </StyledTabTitle>
        </TabItem>
      ))}
    </MaterialTabs>
  );
};

Tabs.defaultProps = {
  onTabChange: noop
};

Tabs.propTypes = {
  /** List of items to be rendered as the tabs */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      icon: PropTypes.node,
      value: PropTypes.string,
      disabled: PropTypes.bool
    })
  ).isRequired,
  /** Set the activeTab */
  selectedTab: PropTypes.string,
  /** Fire the change event when the tab selection change */
  onTabChange: PropTypes.func.isRequired,
  /**Determines additional display behavior of the tabs:
   - scrollable will invoke scrolling properties and allow for horizontally scrolling (or swiping) of the tab bar. 
   - fullWidth will make the tabs grow to use all the available space, which should be used for small views, like on mobile. 
   - standard will render the default state. */
  variant: PropTypes.oneOf(['standard', 'scrollable', 'fullWidth']),
  /**
   * Determine behavior of scroll buttons when tabs are set to scroll:
    - auto will only present them when not all the items are visible. 
    - desktop will only present them on medium and larger viewports. 
    - on will always present them. 
    - off will never present them.
   */
  scrollButtons: PropTypes.oneOf(['auto', 'desktop', 'on', 'off'])
};

export default Tabs;
